/**
 * URL 정규화를 위한 유틸리티 함수들
 */

/**
 * URL 경로에서 중복된 슬래시를 제거하고 정규화하는 함수
 * @param path - 정규화할 URL 경로
 * @returns 정규화된 URL 경로
 *
 * @example
 * normalizePath('//station///maruko86/vod') // '/station/maruko86/vod'
 * normalizePath('////station///maruko86/vod') // '/station/maruko86/vod'
 */
export const normalizePath = (path: string): string => {
  if (!path) {
    return '/';
  }

  // 연속된 슬래시를 하나로 치환
  const normalized = path.replace(/\/+/g, '/');

  // 경로가 '/'로 시작하지 않으면 '/' 추가
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

/**
 * 전체 URL을 정규화하는 함수
 * @param url - 정규화할 전체 URL
 * @returns 정규화된 URL
 *
 * @example
 * normalizeUrl('https://www.sooplive.co.kr////station///maruko86/vod')
 * // 'https://www.sooplive.co.kr/station/maruko86/vod'
 */
export const normalizeUrl = (url: string): string => {
  if (!url) {
    return '';
  }

  try {
    const urlObj = new URL(url);
    urlObj.pathname = normalizePath(urlObj.pathname);
    return urlObj.toString();
  } catch (error) {
    // URL 파싱 실패 시 경로만 정규화
    return normalizePath(url);
  }
};

/**
 * History API의 replaceState에서 사용할 수 있는 안전한 URL을 생성하는 함수
 * @param url - 정규화할 URL
 * @param currentOrigin - 현재 origin (보안 검증용)
 * @returns 안전한 URL 또는 null
 */
export const createSafeHistoryUrl = (
  url: string,
  currentOrigin: string,
): string | null => {
  try {
    const normalizedUrl = normalizeUrl(url);
    const urlObj = new URL(normalizedUrl);

    // origin이 다르면 null 반환 (보안상 위험)
    if (urlObj.origin !== currentOrigin) {
      return null;
    }

    return normalizedUrl;
  } catch (error) {
    console.warn('URL 정규화 실패:', error);
    return null;
  }
};
